import { type FC, useCallback, useMemo, useRef, useState } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";

import Spinner from "../../common/components/Spinner/Spinner";
import ABMonitorSearchBox, { type ABMonitorSearchBoxData } from "./ABMonitorSearchBox/ABMonitorSearchBox";
import ABMonitorResultsTable from "./ABMonitorResultsTable";
import { type ABMonitorRowItem } from "../models";
import { DownloadClickLink } from "../../common/components/Link";
import { type ABmonitorForm, downloadABMonitorReport, ReportType } from "../api/downloadABMonitorReport";
import { getAliases } from "../../common/components/Product/ProductAliases";
import dayjs from "dayjs";
import { ABMonitorLineItemStatus } from "./ABMonitorSearchBox/components/OrderLineStatusFilter";
import { QueryType } from "./ABMonitorSearchBox/components/SearchByInput/SearchByInput";
import { type Subscription } from "rxjs";
import { getABMonitorDataResults } from "../../OrdersQuotes/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { MultiAccountSearchDataProvider } from "../../OrdersQuotes/OrderListPage/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";
import { ABMonitorConfirmedDeliveryStatus } from "~/ABMonitor/components/ABMonitorSearchBox/components/ConfirmedDeliveryLineFilter";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/m3/Alert/Alert";

const ABMonitorPage: FC = () => {
  const { t } = useTranslation();

  const [rows, setRows] = useState<ABMonitorRowItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const subscriptionRef = useRef<Subscription | null>(null);
  const [lastSearchQuery, setLastSearchQuery] = useState<ABMonitorSearchBoxData>();
  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const onSubmitHandler = useCallback((formData: ABMonitorSearchBoxData) => {
    setLastSearchQuery(formData);
    subscriptionRef.current?.unsubscribe();
    setIsLoading(true);
    setIsError(false);
    setRows([]);
    subscriptionRef.current = getABMonitorDataResults(formData).subscribe({
      error: () => {
        setIsLoading(false);
        setIsError(true);
      },
      next: ({ abMonitorData }) => setRows((prev) => prev.concat(abMonitorData)),
      complete: () => setIsLoading(false),
    });
  }, []);

  const filteredRows = useMemo(() => filter(rows, lastSearchQuery), [rows, lastSearchQuery]);

  const newResultCount = filteredRows.filter((row) => row.isNew || row.isUpdated).length;

  const showRows = !isError && rows.length > 0;
  const disableInteractions = isLoading || webservicesDisabled;

  return (
    <>
      <PageTitle title={t("page.abmonitor.title")} />

      {isLoading && <Spinner className="mb-30" />}

      {showRows && (
        <h2 className="headline-small mt-0 mb-30">
          <span>{t("page.abmonitor.resultTitle.total", { count: filteredRows.length })}</span>
          <span> / </span>
          <span>{t("page.abmonitor.resultTitle.new", { count: newResultCount })}</span>
        </h2>
      )}

      <ABMonitorSearchBox onSubmit={onSubmitHandler} className={"mb-30"} disabled={disableInteractions} />

      <div className="d-flex flex-justify-end mb-30">
        <span className="scha__order-list__download-title mr-16">{t("page.abmonitor.downloads.title")}</span>
        <DownloadClickLink
          className="mr-16"
          onClick={downloadABmonitor(ReportType.CSV, lastSearchQuery)}
          data-test-id={"abmonitor-btn-report-csv"}
          disabled={disableInteractions}
        >
          {t("downloadFile.type.csv")}
        </DownloadClickLink>
        <DownloadClickLink
          onClick={downloadABmonitor(ReportType.XLSX, lastSearchQuery)}
          data-test-id={"abmonitor-btn-report-xlsx"}
          disabled={disableInteractions}
        >
          {t("downloadFile.type.excel")}
        </DownloadClickLink>
      </div>

      {showRows && <ABMonitorResultsTable rows={filteredRows} disableSorting={disableInteractions} />}

      {!webservicesDisabled && !isLoading && isError && <Alert severity="error">{t("page.abmonitor.error")}</Alert>}

      {!isLoading && !isError && !filteredRows.length && (
        <h2 className="scha__order-history__total my-0">{t("quoteListPage.noResults")}</h2>
      )}
    </>
  );
};

export function createABMonitorPage(data: { accounts: string; showUnconfirmedLinesFilter: boolean }) {
  return (
    <MultiAccountSearchDataProvider
      accountsJson={data.accounts}
      showUnconfirmedLinesFilter={data.showUnconfirmedLinesFilter}
    >
      <ABMonitorPage />
    </MultiAccountSearchDataProvider>
  );
}

type FilterFunc = (item: ABMonitorRowItem) => boolean;

const FILTERS: { [key: string]: (value: string) => FilterFunc } = {
  [QueryType.PURCHASE_NO]: (value) => (row) => row.customerOrderNo.toLowerCase().includes(value),
  [QueryType.SALES_NO]: (value) => (row) => row.schaefflerOrderNo.toLowerCase().includes(value),
  [QueryType.DESIGNATION]: (value) => (row) => row.product.designation.toLowerCase().includes(value),
  [QueryType.CUSTOMER_MATERIAL_NO]: (value) => (row) =>
    getAliases(row.product, "all").join(", ").toLowerCase().includes(value),
  [QueryType.SAP_MATERIAL_NO]: (value) => (row) => row.product.sapMaterialNumber.toLowerCase().includes(value),
};

function filter(rows: ABMonitorRowItem[], filterConfig?: ABMonitorSearchBoxData) {
  if (!filterConfig) {
    return rows;
  }

  const filters: FilterFunc[] = [];
  if (filterConfig.query) {
    const filterFunc = FILTERS[filterConfig.queryType];
    if (!filterFunc) {
      console.warn("Missing filter function for query type: " + filterConfig.queryType);
    } else {
      filters.push(filterFunc(filterConfig.query.toLowerCase()));
    }
  }

  if (filterConfig.dateFrom) {
    const dateFrom = dayjs(filterConfig.dateFrom);
    filters.push((item) => !dateFrom.isAfter(item.requestedDeliveryDate));
  }

  if (filterConfig.dateTo) {
    const dateTo = dayjs(filterConfig.dateTo);
    filters.push((item) => !dateTo.isBefore(item.requestedDeliveryDate));
  }

  if (filterConfig.lineStatus === ABMonitorLineItemStatus.NEW) {
    filters.push((item) => item.isNew);
  }

  if (filterConfig.lineStatus === ABMonitorLineItemStatus.UPDATED) {
    filters.push((item) => item.isUpdated);
  }

  if (filterConfig.lineStatus === ABMonitorLineItemStatus.NO_CHANGE) {
    filters.push((item) => !item.isUpdated && !item.isNew);
  }

  if (filterConfig.confirmedLines === ABMonitorConfirmedDeliveryStatus.CONFIRMED) {
    filters.push((item) => !!item.curDeliveryDate);
  }

  if (filterConfig.confirmedLines === ABMonitorConfirmedDeliveryStatus.UNCONFIRMED) {
    filters.push((item) => !item.curDeliveryDate);
  }

  return rows.filter((item) => filters.every((filter) => filter(item)));
}

const downloadABmonitor = (reportType: ReportType, filterConfig?: ABMonitorSearchBoxData) => () => {
  if (!filterConfig) {
    return;
  }

  const apiBody: ABmonitorForm = {
    filterByOption: filterConfig.queryType,
    filterByValue: filterConfig ? filterConfig.query : "",
    startDate: filterConfig ? filterConfig.dateFrom : undefined,
    endDate: filterConfig ? filterConfig.dateTo : undefined,
    lineStatus: filterConfig ? filterConfig.lineStatus : undefined,
    deliveryDate: filterConfig ? filterConfig.confirmedLines : undefined,
  };

  downloadABMonitorReport(apiBody, reportType);
};
